<script setup lang="ts">
  import {
    QuestionnairesService,
    OrganisationsService,
    type SuperUserQuestionnaire as Questionnaire,
    type SuperUserOrganisation as Organisation
  } from '@/types/generated'
  import PaginatedTableView, {
    type FetchDataOptions,
    type PaginatedResponse
  } from '@/components/admin/PaginatedTable.vue'
  import { ROUTES } from '@/router/routes'
  import UniqueTableDataCell from '@/components/admin/table/UniqueTableDataCell.vue'
  import TableOptionsButton from '@/components/admin/table/TableOptionsButton.vue'
  import Breadcrumbs from '@/components/admin/breadcrumbs/Breadcrumbs.vue'
  import { TableRow, TableCell } from '@/components/ui/table'
  import { useRouter } from 'vue-router'
  import { onMounted, ref, computed } from 'vue'
  import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
  } from '@/components/ui/dialog'
  import { Button } from '@/components/ui/button'
  import SelectField, { type Option } from '@/components/admin/form/SelectField.vue'
  import { genericErrorToast } from '@/lib/toasts'
  import { Filter } from 'lucide-vue-next'
  import { Badge } from '@/components/ui/badge'
  import { useTranslation } from '@/composables/useTranslation'
  import { useLawTopicTranslation } from '@/composables/useLawTopicTranslation'
  import { useLawFieldTranslation } from '@/composables/useLawFieldTranslation'

  const { t } = useTranslation({
    en: {
      unselectedOption: 'None'
    },
    de: {
      unselectedOption: 'Keine'
    }
  })
  const router = useRouter()
  const { translateLawTopic } = useLawTopicTranslation()
  const { translateLawField } = useLawFieldTranslation()

  const organisations = ref<Organisation[]>([])
  const showDeleteModal = ref(false)
  const showCreateModal = ref(false)
  const selectedOrgForCreation = ref<string>('')
  const questionnaireToDelete = ref<Questionnaire | null>(null)
  const paginatedTableRef = ref<InstanceType<typeof PaginatedTableView> | null>(null)

  // Filter state
  const templateFilter = ref<'all' | 'templates' | 'questionnaires'>('all')
  const showFilters = ref(false)

  const filterOptions = computed<Option[]>(() => [
    { value: 'all', title: 'All Questionnaires' },
    { value: 'templates', title: 'Jupus Templates Only' },
    { value: 'questionnaires', title: 'Regular Questionnaires Only' }
  ])
  // Organization search function
  const searchOrganizations = async (query: string, oldQuery: string) => {
    if (query !== oldQuery) {
      try {
        const result = await OrganisationsService.organisationsList({
          limit: query ? 0 : 20, // Use all results when searching, limit to 20 when not
          search: query || undefined
        })
        organisations.value = result.results as Organisation[]
      } catch (error) {
        console.error('Error searching organizations:', error)
      }
    }
  }

  const organizationOptions = computed<Option[]>(
    () =>
      organisations.value?.map((org) => ({
        value: org.uuid,
        title: org.name || ''
      })) || []
  )

  const handleFilterChange = () => {
    paginatedTableRef.value?.refreshData()
  }

  const clearFilters = () => {
    templateFilter.value = 'all'
    handleFilterChange()
  }

  const fetchQuestionnaires = async (
    options?: FetchDataOptions
  ): Promise<PaginatedResponse<Questionnaire>> => {
    // Determine isJupusTemplate parameter based on filter
    let isJupusTemplate: boolean | undefined = undefined
    if (templateFilter.value === 'templates') {
      isJupusTemplate = true
    } else if (templateFilter.value === 'questionnaires') {
      isJupusTemplate = false
    }

    const response = await QuestionnairesService.questionnairesList({
      limit: options?.limit,
      offset: options?.offset,
      search: options?.search,
      isJupusTemplate: isJupusTemplate
    })

    return response as unknown as PaginatedResponse<Questionnaire>
  }

  const openCreateModal = () => {
    router.push(ROUTES.ADMIN.QUESTIONNAIRES.QUESTIONNAIRE_DETAIL.replace(':uuid', 'new'))
  }

  const closeCreateModal = () => {
    showCreateModal.value = false
    selectedOrgForCreation.value = ''
  }

  const createQuestionnaire = async () => {
    try {
      const isJupusTemplate = !selectedOrgForCreation.value
      const newQuestionnaire = await QuestionnairesService.questionnairesCreate({
        requestBody: {
          title: isJupusTemplate ? 'New Jupus Template' : 'New Questionnaire',
          org_uuid: selectedOrgForCreation.value || null,
          is_jupus_template: isJupusTemplate,
          schema: []
        }
      })
      closeCreateModal()
      router.push(
        ROUTES.ADMIN.QUESTIONNAIRES.QUESTIONNAIRE_DETAIL.replace(':uuid', newQuestionnaire.uuid)
      )
    } catch (error) {
      genericErrorToast()
    }
  }

  const openDeleteModal = (questionnaire: Questionnaire) => {
    questionnaireToDelete.value = questionnaire
    showDeleteModal.value = true
  }

  const closeDeleteModal = () => {
    showDeleteModal.value = false
    questionnaireToDelete.value = null
  }

  const confirmDelete = async () => {
    if (questionnaireToDelete.value) {
      try {
        await QuestionnairesService.questionnairesDestroy({
          uuid: questionnaireToDelete.value.uuid
        })
        closeDeleteModal()
        // Refresh the questionnaire list
        if (paginatedTableRef.value) {
          await paginatedTableRef.value.refreshData()
        }
      } catch (error) {
        genericErrorToast()
      }
    }
  }

  onMounted(async () => {
    const response = await OrganisationsService.organisationsList({ limit: 20 })
    organisations.value = response.results as Organisation[]
  })
</script>

<template>
  <div>
    <div class="space-y-5 p-2 sm:p-5 sm:py-0 md:pt-5">
      <!-- Page Header -->
      <div class="flex items-center justify-between gap-x-5">
        <Breadcrumbs
          :items="[
            { title: 'Dashboard', to: ROUTES.ADMIN.DASHBOARD },
            { title: 'Questionnaires', to: ROUTES.ADMIN.QUESTIONNAIRES.QUESTIONNAIRE_LIST }
          ]"
        />
        <div class="flex items-center justify-end gap-x-2">
          <Button
            variant="outline"
            :class="{ 'border-purple-200 bg-purple-50': showFilters }"
            @click="showFilters = !showFilters"
          >
            <Filter class="mr-2 h-4 w-4" />
            Filters
            <Badge
              v-if="templateFilter !== 'all'"
              class="ml-2 bg-purple-100 text-purple-700"
            >
              {{ templateFilter === 'templates' ? 'Templates' : 'Questionnaires' }}
            </Badge>
          </Button>
          <Button
            variant="default"
            @click="openCreateModal"
          >
            <svg
              class="hidden size-3 flex-shrink-0 sm:block"
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              fill="currentColor"
              viewBox="0 0 16 16"
            >
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M8 1C8.55228 1 9 1.44772 9 2V7L14 7C14.5523 7 15 7.44771 15 8C15 8.55228 14.5523 9 14 9L9 9V14C9 14.5523 8.55228 15 8 15C7.44772 15 7 14.5523 7 14V9.00001L2 9.00001C1.44772 9.00001 1 8.5523 1 8.00001C0.999999 7.44773 1.44771 7.00001 2 7.00001L7 7.00001V2C7 1.44772 7.44772 1 8 1Z"
              ></path>
            </svg>
            Create questionnaire
          </Button>
        </div>
      </div>
      <!-- End Page Header -->

      <!-- Filters Section -->
      <div
        v-if="showFilters"
        class="rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
      >
        <div class="flex flex-wrap items-center gap-4">
          <div class="min-w-[200px]">
            <SelectField
              v-model="templateFilter"
              label="Filter by Type"
              name="templateFilter"
              :options="filterOptions"
              @update:model-value="handleFilterChange"
            />
          </div>
          <div class="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              @click="clearFilters"
            >
              Clear Filters
            </Button>
          </div>
        </div>
      </div>

      <!-- Users Table Card -->
      <div class="flex flex-col space-y-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <div>
          <!-- Tab Content -->
          <div class="">
            <!-- Table Section -->
            <div
              class="[&amp;::-webkit-scrollbar]:h-2 [&amp;::-webkit-scrollbar-thumb]:rounded-full
                [&amp;::-webkit-scrollbar-track]:bg-gray-100 [&amp;::-webkit-scrollbar-thumb]:bg-gray-300
                &amp;::-webkit-scrollbar-track]:bg-neutral-700 &amp;::-webkit-scrollbar-thumb]:bg-neutral-500
                overflow-x-auto"
            >
              <div class="inline-block min-w-full align-middle">
                <!-- Table -->
                <PaginatedTableView
                  ref="paginatedTableRef"
                  :fetchData="fetchQuestionnaires"
                  searchableFields="UUID, title, description, customer, category, law field, law topic"
                >
                  <template #table-head>
                    <TableCell>UUID</TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell class="min-w-[300px]">Organization</TableCell>
                    <TableCell>Law field</TableCell>
                    <TableCell>Law topic</TableCell>
                    <TableCell></TableCell>
                  </template>
                  <template #table-row="{ items: questionnaires }: { items: Questionnaire[] }">
                    <TableRow
                      v-for="questionnaire in questionnaires"
                      :key="questionnaire.uuid"
                    >
                      <UniqueTableDataCell :truncate="true">
                        <router-link
                          :to="
                            ROUTES.ADMIN.QUESTIONNAIRES.QUESTIONNAIRE_DETAIL.replace(
                              ':uuid',
                              questionnaire.uuid
                            )
                          "
                        >
                          {{ questionnaire.uuid }}
                        </router-link>
                      </UniqueTableDataCell>
                      <TableCell>
                        <div class="flex items-center gap-2">
                          <span>{{ questionnaire.title }}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {{ questionnaire.description }}
                      </TableCell>
                      <TableCell>
                        <template v-if="questionnaire.org?.name">
                          {{ questionnaire.org?.name || 'Unknown Organization' }}
                        </template>
                        <template v-else-if="questionnaire.is_jupus_template">
                          <span
                            class="inline-flex items-center rounded-full bg-purple-50 px-2 py-1 text-xs font-medium text-purple-700
                              ring-1 ring-inset ring-purple-700/10"
                          >
                            Jupus Template
                          </span>
                        </template>
                        <template v-else>
                          <span class="text-gray-500">
                            LEGACY (You probably shouldn't update this)
                          </span>
                        </template>
                      </TableCell>
                      <TableCell>
                        {{ translateLawField(questionnaire.law_field) }}
                      </TableCell>
                      <TableCell>
                        {{ translateLawTopic(questionnaire.law_topic) }}
                      </TableCell>
                      <TableCell>
                        <TableOptionsButton
                          :options="[
                            {
                              label: 'Edit',
                              to: ROUTES.ADMIN.QUESTIONNAIRES.QUESTIONNAIRE_DETAIL.replace(
                                ':uuid',
                                questionnaire.uuid
                              )
                            },
                            {
                              label: 'Duplicate',
                              onClick: async () => {
                                const copy = await QuestionnairesService.questionnairesCopyCreate({
                                  uuid: questionnaire.uuid
                                })
                                if (copy) {
                                  router.push(
                                    ROUTES.ADMIN.QUESTIONNAIRES.QUESTIONNAIRE_DETAIL.replace(
                                      ':uuid',
                                      copy.uuid
                                    )
                                  )
                                }
                              }
                            },
                            {
                              label: 'Delete',
                              onClick: () => openDeleteModal(questionnaire)
                            }
                          ]"
                        />
                      </TableCell>
                    </TableRow>
                  </template>
                </PaginatedTableView>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Create Questionnaire Modal -->
    <Dialog
      :open="showCreateModal"
      @update:open="closeCreateModal"
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create New Questionnaire</DialogTitle>
          <DialogDescription>
            Choose the organization for this questionnaire, or leave empty to create a Jupus
            template.
          </DialogDescription>
        </DialogHeader>
        <div class="space-y-4">
          <SelectField
            v-model="selectedOrgForCreation"
            label="Organization (Optional)"
            name="organization"
            :options="organizationOptions"
            :is-filterable="true"
            placeholder="Select an organization or leave empty for Jupus template"
            :input-action="searchOrganizations"
            :allow-unselect="true"
            :unselected-option-text="t('unselectedOption')"
          />
        </div>
        <DialogFooter>
          <Button
            variant="secondary"
            @click="closeCreateModal"
          >
            Cancel
          </Button>
          <Button
            variant="default"
            @click="createQuestionnaire"
          >
            Create Questionnaire
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <!-- Delete Confirmation Modal -->
    <Dialog
      :open="showDeleteModal"
      @update:open="closeDeleteModal"
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Confirm Deletion</DialogTitle>
          <DialogDescription>
            Are you sure you want to delete the questionnaire "{{ questionnaireToDelete?.title }}"?
            This action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="secondary"
            @click="closeDeleteModal"
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            @click="confirmDelete"
          >
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </div>
</template>

<style scoped></style>
